import loadable from '@loadable/component';
import { toFactor } from '@/utilities/formatting';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import {
  PORTFOLIO_STATIC_COMPANY,
  PORTFOLIO_INTRINSIC_VALUE,
  PORTFOLIO_PE,
  PORTFOLIO_PEG,
  PORTFOLIO_PB,
} from '@/constants/feedbackSections';
import {
  PORTFOLIO_DOWNLOAD_INTRINSIC_VALUE,
  PORTFOLIO_DOWNLOAD_PE_PEG_GAUGE,
} from '@/constants/downloadSections';
import { getPortfolioInfographicDownloadUrl } from '@utilities/urlGenerator';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
const NoData = loadable(() => import('@components/NoData'), {
  resolveComponent: (component) => component.NoData,
});
const PEGRatioGauge = loadable(() => import('../PEGRatioGauge'), {
  ssr: false,
});
const PBRatioGauge = loadable(() => import('../PBRatioGauge'), { ssr: false });
const PERatioGauge = loadable(() => import('../PERatioGauge'), { ssr: false });
const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);
const DCF = loadable(() => import('@components/DCF'), { ssr: false });

export interface ValuationProps {
  portfolioName: string;
  portfolioId: string;
  secretCode?: string;
  sectionNumber?: number;
  currencyISO: string;
  intrinsicDiscount: number;
  intrinsicDiscountFormatted: string;
  totalFreeCashFlowValue: number;
  totalFreeCashFlowDiscount: number;
  totalFreeCashFlowValueFormatted: string;
  totalFreeCashFlowDiscountFormatted: string;
  marketName: string;
  PB?: number | null;
  marketPB?: number;
  PEG?: number | null;
  marketPEG?: number;
  PE?: number | null;
  marketPE?: number;
  ROA?: number | null;
  marketROA?: number;
  ROE?: number | null;
  marketROE?: number;
}

function calculateKeyMetric(PB?: null | number, PE?: null | number) {
  if (PE && PE > 0) {
    return {
      keymetricTitle: 'Price Based on Earnings (PE)',
      value: toFactor(PE, 2),
    };
  } else if (PB && PB > 0) {
    return {
      keymetricTitle: 'Price Based on Value of Assets (PB)',
      value: toFactor(PB, 2),
    };
  } else {
    return null;
  }
}

function Valuation({
  PB,
  PE,
  PEG,
  marketName,
  marketPB,
  marketPE,
  marketPEG,
  portfolioId,
  portfolioName,
  secretCode,
  sectionNumber,
  totalFreeCashFlowDiscount,
  totalFreeCashFlowValue,
  intrinsicDiscount,
  intrinsicDiscountFormatted,
  totalFreeCashFlowValueFormatted,
  totalFreeCashFlowDiscountFormatted,
}: ValuationProps) {
  const keyMetrics = calculateKeyMetric(PB, PE);
  const downloadIntrinsicValueUrl = getPortfolioInfographicDownloadUrl(
    portfolioId,
    PORTFOLIO_DOWNLOAD_INTRINSIC_VALUE,
    secretCode || ''
  );

  const downloadGaugeUrl = getPortfolioInfographicDownloadUrl(
    portfolioId,
    PORTFOLIO_DOWNLOAD_PE_PEG_GAUGE,
    secretCode || ''
  );

  return (
    <ReportSection sectionNumber={sectionNumber}>
      <ReportSectionHeader
        title="Valuation"
        sectionDescription="The value of the Portfolio based on future cash flows (where available) and price relative to the market"
        keyMetricTitle={
          keyMetrics !== null ? keyMetrics.keymetricTitle : undefined
        }
        keyMetric={keyMetrics !== null ? keyMetrics.value : undefined}
      />
      <ReportSubSection
        title="Intrinsic Value based on Free Cash Flows"
        trackingProps={{ subject: 'chart' }}
        body={
          totalFreeCashFlowValue && totalFreeCashFlowDiscount ? (
            <DeferRenderUntilIntersecting preRenderHeight={358}>
              {() => (
                <>
                  <DCF
                    sharePrice={totalFreeCashFlowValue}
                    sharePriceFormatted={totalFreeCashFlowValueFormatted}
                    intrinsicValue={totalFreeCashFlowDiscount}
                    intrinsicValueFormatted={totalFreeCashFlowDiscountFormatted}
                    intrinsicDiscount={intrinsicDiscount}
                    intrinsicDiscountFormatted={intrinsicDiscountFormatted}
                    showNewStyle={false}
                  />
                  <ChartActionsContainer
                    companyName={PORTFOLIO_STATIC_COMPANY}
                    feedbackLabel={PORTFOLIO_INTRINSIC_VALUE}
                    exportDarkUrl={downloadIntrinsicValueUrl}
                  />
                </>
              )}
            </DeferRenderUntilIntersecting>
          ) : (
            <NoData />
          )
        }
      />
      <hr />
      <ReportSubSection
        title="Price Based on Earnings"
        trackingProps={{ subject: 'chart' }}
        body={
          typeof PE === 'number' && typeof marketPE === 'number' ? (
            <DeferRenderUntilIntersecting preRenderHeight={335}>
              {() => (
                <>
                  <PERatioGauge
                    portfolioPE={PE}
                    portfolioName={portfolioName}
                    marketPE={marketPE}
                    marketName={marketName}
                  />
                  <ChartActionsContainer
                    companyName={PORTFOLIO_STATIC_COMPANY}
                    feedbackLabel={PORTFOLIO_PE}
                    exportDarkUrl={downloadGaugeUrl}
                  />
                </>
              )}
            </DeferRenderUntilIntersecting>
          ) : (
            <NoData />
          )
        }
      />
      <hr />
      <ReportSubSection
        title="Price Based on Expected Growth"
        trackingProps={{ subject: 'chart' }}
        body={
          typeof PEG === 'number' && typeof marketPEG === 'number' ? (
            <DeferRenderUntilIntersecting preRenderHeight={335}>
              {() => (
                <>
                  <PEGRatioGauge
                    portfolioPEG={PEG}
                    portfolioName={portfolioName}
                    marketPEG={marketPEG}
                    marketName={marketName}
                  />
                  <ChartActionsContainer
                    companyName={PORTFOLIO_STATIC_COMPANY}
                    feedbackLabel={PORTFOLIO_PEG}
                    exportDarkUrl={downloadGaugeUrl}
                  />
                </>
              )}
            </DeferRenderUntilIntersecting>
          ) : (
            <NoData />
          )
        }
      />
      <hr />
      <ReportSubSection
        title="Price Based on Value of Assets"
        trackingProps={{ subject: 'chart' }}
        body={
          typeof PB === 'number' && typeof marketPB === 'number' ? (
            <DeferRenderUntilIntersecting preRenderHeight={335}>
              {() => (
                <>
                  <PBRatioGauge
                    portfolioPB={PB}
                    portfolioName={portfolioName}
                    marketPB={marketPB}
                    marketName={marketName}
                  />
                  <ChartActionsContainer
                    companyName={PORTFOLIO_STATIC_COMPANY}
                    feedbackLabel={PORTFOLIO_PB}
                    exportDarkUrl={downloadGaugeUrl}
                  />
                </>
              )}
            </DeferRenderUntilIntersecting>
          ) : (
            <NoData />
          )
        }
      />
    </ReportSection>
  );
}

export default Valuation;
